import { useQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Members } from '@air/api';
import { type BoardAncestors, type WorkspaceMemberResponseWithRoles, WorkspaceMemberType } from '@air/api/types';
import { formatFullName, ONE_HOUR } from '@air/utilities';

import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';
import { useURLClipIdSelector } from '~/hooks/useURLClipIdSelector';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedClipBoardsSelector, centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { isActiveMember } from '~/swr-hooks/members/utils/memberUtils';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

export const MEMBERS_FOR_MENTIONS = 'MEMBERS_FOR_MENTIONS';

export const getMembersForMentionsKey = (workspaceId?: string) => {
  return [MEMBERS_FOR_MENTIONS, { workspaceId }];
};

export const useGetDiscussionMentionsMembers = ({
  boardId,
  ancestors,
  workspaceId,
}: {
  boardId?: string;
  ancestors: BoardAncestors;
  workspaceId: string;
}) => {
  const { data: account } = useAccountContext();
  const { data: workspaces } = useWorkspaces(undefined, { enabled: !!account && account.type !== 'anonymous' });
  // The clip's own boards (and their ancestors) define who can be mentioned, regardless of how the user
  // navigated to the asset. Without this, opening an asset from a non-board URL (saved search, /all,
  // search results) leaves the URL boardId undefined and the centralizedBoard ancestors empty, so every
  // guest gets filtered out — even if they have access to the board the asset lives on.
  const urlClipId = useURLClipIdSelector();
  const centralizedClipId = useSelector(centralizedClipIdSelector);
  const centralizedClipBoards = useSelector(centralizedClipBoardsSelector);
  const isCentralizedClipCurrent = !urlClipId || centralizedClipId === urlClipId;
  const clipBoards = isCentralizedClipCurrent ? centralizedClipBoards : [];

  const isWorkspaceMember = workspaces?.some((w) => w.id === workspaceId);

  const membersQueryEnabled = !!account && !!workspaceId && !!isWorkspaceMember;
  const { data: members } = useQuery({
    queryKey: getMembersForMentionsKey(workspaceId),

    queryFn: async () => {
      const { members } = await Members.list({
        workspaceId,
        options: {
          memberTypes: [WorkspaceMemberType.member, WorkspaceMemberType.guest],
        },
      });

      return members;
    },
    enabled: membersQueryEnabled,
    staleTime: ONE_HOUR,
  });

  const settledMembers = members ?? [];
  const activeMembers = settledMembers.filter((member) => isActiveMember(member) && member.type !== 'guest');
  const activeGuests = settledMembers.filter((member) => isActiveMember(member) && member.type === 'guest');

  // Full workspace members can mention anyone in the workspace; guests stay scoped to the boards the
  // asset lives on (COR-1937). Default to the scoped path unless the caller is positively identified as
  // a non-guest member, so a caller we can't classify is never over-exposed to the whole workspace.
  const currentMember = settledMembers.find((member) => member.accountId === account?.id);
  const callerCanMentionEntireWorkspace = !!currentMember && currentMember.type !== WorkspaceMemberType.guest;
  // True while the upstream chain (account → workspaces → members → current clip) hasn't produced
  // a definitive answer yet. Consumers gate the `@` trigger surface on this so
  // react-mentions can't snapshot an empty or stale data array at trigger time — once it
  // snapshots, it doesn't refresh the open dropdown when `data` later changes.
  const isLoadingMentions =
    !account ||
    (account.type !== 'anonymous' && !workspaces) ||
    (membersQueryEnabled && members === undefined) ||
    (!callerCanMentionEntireWorkspace && !isCentralizedClipCurrent);

  const mentionMembers = useMemo<MentionsData[]>(() => {
    const toMentionsData = ({
      firstName,
      lastName,
      avatar,
      accountId,
      roles,
      type,
    }: WorkspaceMemberResponseWithRoles): MentionsData => ({
      id: accountId,
      display: formatFullName(firstName, lastName) ?? '',
      avatar,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      type,
      roles,
    });

    if (callerCanMentionEntireWorkspace) {
      return uniqBy([...activeMembers, ...activeGuests], 'accountId').map(toMentionsData);
    }

    const accessibleBoardIds = new Set<string>();

    if (boardId) {
      accessibleBoardIds.add(boardId);
    }
    for (const ancestor of ancestors) {
      accessibleBoardIds.add(ancestor.id);
    }
    for (const board of clipBoards) {
      accessibleBoardIds.add(board.id);
      for (const ancestor of board.ancestors ?? []) {
        accessibleBoardIds.add(ancestor.id);
      }
    }

    return uniqBy(
      [
        ...activeMembers,
        ...activeGuests.filter(({ roles }) => roles.guest.find((role) => accessibleBoardIds.has(role.boardId))),
      ],
      'accountId',
    ).map((member) => ({
      ...toMentionsData(member),
      roles: {
        ...member.roles,
        guest: member.roles.guest.filter((role) => accessibleBoardIds.has(role.boardId)),
      },
    }));
  }, [callerCanMentionEntireWorkspace, activeMembers, activeGuests, boardId, ancestors, clipBoards]);

  return { mentionMembers, isLoadingMentions };
};
